import React, { useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

/**
 * Spinner Component
 * 
 * A unified loading spinner component that replaces all spinner implementations
 * across the application. Follows the 80/20 principle with consistent behavior
 * and configurable variations for different use cases.
 * 
 * This component provides semantic loading states, accessibility features,
 * and optimized performance through memoization.
 * 
 * @example
 * // Basic inline usage
 * <Spinner />
 * 
 * @example
 * // Search results loading
 * <Spinner 
 *   variant="search" 
 *   size="large" 
 *   message="Searching for products..." 
 * />
 * 
 * @example
 * // Button loading state
 * <button disabled={loading}>
 *   {loading ? <Spinner variant="button" /> : 'Submit'}
 * </button>
 * 
 * @example
 * // Page loading with custom styling
 * <Spinner
 *   variant="page"
 *   size="large"
 *   message="Loading page content..."
 *   color="text-blue-500"
 *   className="my-8"
 * />
 * 
 * @example
 * // Inline with text
 * <div>
 *   <Spinner variant="inline" size="small" message="Saving..." />
 * </div>
 * 
 * @param {Object} props - Component props
 * @param {('search'|'button'|'page'|'inline')} [props.variant='inline'] - Spinner variant for different contexts
 * @param {('small'|'medium'|'large')} [props.size='medium'] - Size of the spinner and container
 * @param {string} [props.message] - Optional message to display with the spinner
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles object
 * @param {string} [props.color='text-primary'] - Spinner color class (Tailwind classes)
 * @param {boolean} [props.animate=true] - Whether to animate the spinner rotation
 * @param {string} [props.ariaLabel] - Accessibility label for screen readers
 * @param {string} [props.role='status'] - ARIA role for the loading state
 * @returns {React.Element} The rendered spinner component
 */
const Spinner = ({
  // Core configuration props
  variant = 'inline',
  size = 'medium',
  message = '',
  
  // Styling props
  className = '',
  style = {},
  color = 'text-primary',
  
  // Behavior props
  animate = true,
  
  // Accessibility props
  ariaLabel,
  role = 'status',
  
  // Rest props for flexibility
  ...restProps
}) => {
  // Memoized size configuration
  const sizeConfig = useMemo(() => ({
    small: {
      iconSize: 16,
      containerPadding: 'p-2',
      textSize: 'text-sm',
      spacing: 'space-y-2'
    },
    medium: {
      iconSize: 24,
      containerPadding: 'p-4',
      textSize: 'text-base',
      spacing: 'space-y-3'
    },
    large: {
      iconSize: 48,
      containerPadding: 'p-6',
      textSize: 'text-lg',
      spacing: 'space-y-4'
    }
  }), []);

  // Memoized current size configuration
  const currentSizeConfig = useMemo(() => {
    return sizeConfig[size] || sizeConfig.medium;
  }, [sizeConfig, size]);

  // Memoized animation class
  const animationClass = useMemo(() => {
    return animate ? 'animate-spin' : '';
  }, [animate]);

  // Memoized variant-specific configurations
  const variantConfig = useMemo(() => ({
    search: {
      containerClasses: `flex flex-col items-center justify-center ${currentSizeConfig.containerPadding}`,
      iconSize: currentSizeConfig.iconSize * 2, // Larger for search
      iconClasses: 'mx-auto',
      showMessage: !!message,
      messageClasses: `${currentSizeConfig.textSize} font-medium text-muted-foreground text-center`,
      wrapperClasses: currentSizeConfig.spacing,
      useWrapper: true,
      semanticRole: 'status',
      ariaLive: 'polite'
    },
    button: {
      containerClasses: 'flex justify-center items-center',
      iconSize: 20, // Fixed size for buttons
      iconClasses: 'flex-shrink-0',
      showMessage: false,
      messageClasses: '',
      wrapperClasses: '',
      useWrapper: false,
      semanticRole: 'status',
      ariaLive: 'assertive'
    },
    page: {
      containerClasses: `flex items-center justify-center py-12`,
      iconSize: currentSizeConfig.iconSize,
      iconClasses: 'mx-auto',
      showMessage: !!message,
      messageClasses: `${currentSizeConfig.textSize} text-center text-muted-foreground`,
      wrapperClasses: currentSizeConfig.spacing,
      useWrapper: true,
      semanticRole: 'status',
      ariaLive: 'polite'
    },
    inline: {
      containerClasses: 'inline-flex items-center gap-2',
      iconSize: currentSizeConfig.iconSize,
      iconClasses: 'flex-shrink-0',
      showMessage: !!message,
      messageClasses: currentSizeConfig.textSize,
      wrapperClasses: '',
      useWrapper: false,
      semanticRole: 'status',
      ariaLive: 'polite'
    }
  }), [currentSizeConfig, message]);

  // Current variant configuration
  const currentVariantConfig = useMemo(() => {
    return variantConfig[variant] || variantConfig.inline;
  }, [variantConfig, variant]);

  // Memoized spinner icon with proper accessibility
  const spinnerIcon = useMemo(() => (
    <Loader2 
      className={`${animationClass} ${color} ${currentVariantConfig.iconClasses}`}
      size={currentVariantConfig.iconSize}
      aria-hidden="true"
      role="presentation"
    />
  ), [animationClass, color, currentVariantConfig.iconClasses, currentVariantConfig.iconSize]);

  // Memoized message content with accessibility
  const messageContent = useMemo(() => {
    if (!currentVariantConfig.showMessage) return null;

    return (
      <div 
        className={currentVariantConfig.messageClasses}
        aria-live={currentVariantConfig.ariaLive}
      >
        {variant === 'search' ? (
          <p className="animate-pulse">{message}</p>
        ) : (
          <span>{message}</span>
        )}
      </div>
    );
  }, [currentVariantConfig.showMessage, currentVariantConfig.messageClasses, 
      currentVariantConfig.ariaLive, variant, message]);

  // Memoized spinner content structure
  const spinnerContent = useMemo(() => {
    if (currentVariantConfig.useWrapper) {
      return (
        <div className={currentVariantConfig.wrapperClasses}>
          {spinnerIcon}
          {messageContent}
        </div>
      );
    }

    return (
      <>
        {spinnerIcon}
        {messageContent}
      </>
    );
  }, [currentVariantConfig.useWrapper, currentVariantConfig.wrapperClasses, 
      spinnerIcon, messageContent]);

  // Memoized combined className
  const combinedClassName = useMemo(() => {
    return [
      'spinner', // Base class for styling and testing
      `spinner-${variant}`, // Variant-specific class
      `spinner-${size}`, // Size-specific class
      currentVariantConfig.containerClasses,
      className // User-provided classes last
    ].filter(Boolean).join(' ');
  }, [variant, size, currentVariantConfig.containerClasses, className]);

  // Memoized accessibility label
  const accessibilityLabel = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    
    const defaultLabels = {
      search: message || 'Searching...',
      button: 'Loading',
      page: message || 'Loading page content',
      inline: message || 'Loading'
    };
    
    return defaultLabels[variant] || 'Loading';
  }, [ariaLabel, message, variant]);

  // Error handling for invalid props
  if (!['search', 'button', 'page', 'inline'].includes(variant)) {
    console.warn(`Spinner: Invalid variant "${variant}". Using "inline" as fallback.`);
  }

  if (!['small', 'medium', 'large'].includes(size)) {
    console.warn(`Spinner: Invalid size "${size}". Using "medium" as fallback.`);
  }

  return (
    <div 
      className={combinedClassName}
      style={style}
      role={role}
      aria-label={accessibilityLabel}
      aria-live={currentVariantConfig.ariaLive}
      aria-busy="true"
      {...restProps}
    >
      {spinnerContent}
    </div>
  );
};

// Comprehensive PropTypes validation
Spinner.propTypes = {
  // Core configuration
  variant: PropTypes.oneOf(['search', 'button', 'page', 'inline']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  message: PropTypes.string,
  
  // Styling props
  className: PropTypes.string,
  style: PropTypes.object,
  color: PropTypes.string,
  
  // Behavior props
  animate: PropTypes.bool,
  
  // Accessibility props
  ariaLabel: PropTypes.string,
  role: PropTypes.string
};

// Default props for documentation and consistency
Spinner.defaultProps = {
  variant: 'inline',
  size: 'medium',
  message: '',
  className: '',
  style: {},
  color: 'text-primary',
  animate: true,
  role: 'status'
};

export default Spinner; 